import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Beneficjent } from '../../models/beneficjent';
import { CzlonekRodziny } from '../../models/czlonek-rodziny';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SlownikResponse } from '../../models/slownik-response';
import { NotyfikacjeBeneficjenta } from '../../models/notyfikacje-beneficjenta';
import { HttpUtils } from '../../utils/http.utils';
import { TableRequestData } from '../../models/table/table-request-param';
import { AnnouncementUser } from '../../models/announcement-user';

@Injectable({
  providedIn: 'root',
})
export class UserApi {
  #httpClient: HttpClient = inject(HttpClient);
  readonly #apiUrl = environment.API_EWNIOSKI + '/user';

  public getUser$(): Observable<Beneficjent> {
    return this.#httpClient.get<Beneficjent>(`${this.#apiUrl}`, {
      withCredentials: true,
    });
  }

  public saveUserNotificationSettings$(
    notyfikacjeBeneficjenta: NotyfikacjeBeneficjenta,
  ): Observable<any> {
    return this.#httpClient.post<SlownikResponse>(
      `${this.#apiUrl}/notificationsSettings`,
      notyfikacjeBeneficjenta,
      {
        withCredentials: true,
      },
    );
  }

  public saveUser$(beneficjent: Beneficjent): Observable<any> {
    return this.#httpClient.post<void>(`${this.#apiUrl}`, beneficjent, {
      withCredentials: true,
    });
  }

  public createUser$(beneficjent: Beneficjent): Observable<void> {
    return this.#httpClient.post<void>(`${this.#apiUrl}/create`, beneficjent, {
      withCredentials: true,
    });
  }

  public saveFamilyMember$(czlonekRodziny: CzlonekRodziny): Observable<any> {
    return this.#httpClient.post<void>(
      `${this.#apiUrl}/familyMember`,
      czlonekRodziny,
      {
        withCredentials: true,
      },
    );
  }

  public getFamilyMember$(id: number): Observable<CzlonekRodziny> {
    const httpParams = new HttpParams().append('id', id);
    return this.#httpClient.get<CzlonekRodziny>(
      `${this.#apiUrl}/familyMember`,
      {
        params: httpParams,
        withCredentials: true,
      },
    );
  }

  public getFamilyMembers$(tableRequest: TableRequestData): Observable<any> {
    return this.#httpClient.get<void>(`${this.#apiUrl}/familyMembers`, {
      params: HttpUtils.toHttpParams(tableRequest),
      withCredentials: true,
    });
  }

  public deleteFamilyMember$(familyMemberId: number): Observable<void> {
    const httpParams = new HttpParams().append('id', familyMemberId);
    return this.#httpClient.delete<void>(`${this.#apiUrl}/familyMember`, {
      params: httpParams,
      withCredentials: true,
    });
  }

  public deactivateAccount$(userId: number): Observable<void> {
    const params = new HttpParams().append('id', userId);

    return this.#httpClient.put<void>(
      `${this.#apiUrl}/deactivateAccount`,
      null,
      {
        params,
        withCredentials: true,
      },
    );
  }

  public verifyEmail$(): Observable<void> {
    return this.#httpClient.post<void>(
      `${this.#apiUrl}/verifyEmail`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  public verifyUserHash$(hash: string): Observable<void> {
    const httpParams = new HttpParams().append('hash', hash);
    return this.#httpClient.post<void>(
      `${this.#apiUrl}/verifyHash`,
      {},
      {
        params: httpParams,
      },
    );
  }

  public verifyData$(): Observable<void> {
    return this.#httpClient.post<void>(
      `${this.#apiUrl}/verifyData`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  public announcementsEmailConsent$(): Observable<void> {
    return this.#httpClient.post<void>(
      `${this.#apiUrl}/announcementsEmailConsent`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  getAnnouncementsForUser$(): Observable<AnnouncementUser> {
    return this.#httpClient.get<AnnouncementUser>(
      `${this.#apiUrl}/announcements`,
      {
        withCredentials: true,
      },
    );
  }

  public confirmAnnouncement$(id: number): Observable<void> {
    return this.#httpClient.put<void>(
      `${this.#apiUrl}/announcement/confirm/${id}`,
      {},
      { withCredentials: true },
    );
  }
}
